// English catalogue — the source of truth for keys.
//
// ko.js mirrors this shape. A key missing from ko falls back to the string
// here rather than to the key itself, so Korean can be filled in incrementally
// without shipping `rules.cadence.title` to a reader.
//
// `_one` / `_other` variants exist for English's sake. Korean has no plural
// forms and supplies a single string, which the `tn()` helper handles.

const en = {
    // Values that arrive from the API as free text — FMP's sector taxonomy and
    // the brokers' asset-type strings. Keyed by the raw value, and looked up
    // through td() rather than t(), which falls back to the raw string so a
    // sector FMP starts returning tomorrow shows up rather than disappearing.
    //
    // English is mostly identity, so only the values worth rewording are here.
    data: {
        sector: {},
        assetType: {
            'Security type is not defined': 'Unclassified',
        },
    },

    lang: { label: 'Language', viewsAria: 'Portfolio views' },

    nav: {
        totalPortfolio: 'Total Portfolio',
        artCollection: 'art collection',
        transactions: 'Transactions',
        notes: 'Notes',
        kanoseiIndex: 'Kanosei Index',
        cryptoHoldings: 'Crypto Holdings',
        privateInvestments: 'Private Investments',
        stockInvestments: 'Stock Investments',
        admin: 'Admin',
        settings: 'Settings',
        portfolio: 'portfolio',
        // The sidebar renders on the signed-in pages too, so its copy is in
        // scope even though the same line is the public homepage's hero.
        tagline: 'A private investment fund',
    },

    common: {
        settings: 'settings',
        signOut: 'sign out',
        signIn: 'Sign in',
        portfolio: 'Portfolio',
        backToPortfolio: 'back to portfolio',
        all: 'All',
        table: 'Table',
        heatmap: 'Heatmap',
        filter: 'filter…',
        save: 'save',
        cancel: 'cancel',
        edit: 'edit',
        delete: 'delete',
        asOf: 'as of {when}',
        loadingError: 'Could not load this view.',
    },

    holdings: {
        title: 'Portfolio',
        error: 'Could not load the portfolio.',
        emptyBefore: 'No positions yet. Link a brokerage account in',
        emptyLink: 'settings',
        emptyAfter: '.',
        refresh: 'refresh from broker',
        refreshing: 'refreshing, this takes a moment…',
        refreshFailed: 'Could not refresh just now — {reason} The figures below are the last good sync.',
        notices_one: '{count} notice — read and dismiss them in Notes',
        notices_other: '{count} notices — read and dismiss them in Notes',
    },

    private: {
        title: 'Private Investments',
        scope: 'Venture and DAO positions, kept by hand. Only active investments count towards the Total Portfolio — an exit\'s proceeds are already in another account.',
        active: 'Active Investments',
        exits: 'Exits',
        count_one: '{count} investment',
        count_other: '{count} investments',
        entries_one: '{count} entry',
        entries_other: '{count} entries',
        add: '+ add investment',
        removeRound: 'remove',
        delete: 'delete investment',
        confirmDelete: 'Delete {name} and all its entries? This cannot be undone.',
        edit: 'edit',
        editAria: 'Edit {name}',
        toExits: 'move to exits',
        toExitsAria: 'Move {name} to Exits',
        toActive: 'move to active',
        toActiveAria: 'Move {name} to Active Investments',
        na: 'N/A',
        yes: 'Y',
        no: 'N',
        usdPlaceholder: 'USD',
        quantityPlaceholder: 'amount',
        assetPlaceholder: 'asset',
        quantityAria: '{field} amount',
        markQuantityAria: 'Unrealised mark amount',
        markAssetAria: 'Unrealised mark asset',
        sectorPlaceholder: 'sector',
        subSectorPlaceholder: 'sub-category',
        sectorAria: 'Sector',
        subSectorAria: 'Sub-category',
        emptyActive: 'No active investments.',
        emptyExits: 'No exits yet.',
        live: 'live',
        holdings_one: '{count} holding',
        holdings_other: '{count} holdings',
        kinds: { VC: 'VC', DAO: 'DAO', Fund: 'Fund', 'Real Estate': 'Real Estate' },
        col: {
            name: 'Investment',
            kind: 'Type',
            sector: 'Sector',
            invested: 'Initial cost basis',
            distributed: 'Realised P/L / distributions',
            mark: 'Unrealised mark',
            pnl: 'Total P/L',
            realised: 'Realised P/L',
            comments: 'Comments',
            actions: 'Actions',
        },
        round: {
            description: 'Description',
            date: 'Date',
            amount: 'Amount (USD)',
            asset: 'Asset used',
            valuation: 'Valuation',
            units: 'Units',
            aria: '{field} {n}',
            add: '+ add cost basis entry',
            removeAria: 'Remove cost basis entry {n}',
        },
        dist: {
            description: 'Description',
            date: 'Date',
            amount: 'Amount (USD)',
            asset: 'Asset received',
            sold: 'Sold?',
            soldInto: 'Asset sold into',
            aria: 'Distribution {n} {field}',
            add: '+ add distribution',
            removeAria: 'Remove distribution {n}',
        },
        summary: {
            mark: 'Unrealised mark',
            returned: 'Returned',
            invested: 'Invested',
            counts_one: '{count} investment · {rounds} rounds',
            counts_other: '{count} investments · {rounds} rounds',
            pnl: 'Total P/L',
            withDistributed: 'incl. {amount} distributed',
            realised: 'Realised P/L',
            multiple: 'Multiple',
            unmarked_one: '{count} investment has no mark yet.',
            unmarked_other: '{count} investments have no mark yet.',
            unreturned_one: '{count} exit has no distribution recorded.',
            unreturned_other: '{count} exits have no distribution recorded.',
        },
        errors: {
            load: 'Could not load the private investments.',
            save: 'Could not save the investment.',
            move: 'Could not move the investment.',
            delete: 'Could not delete the investment.',
            invalid: 'Check the investment details.',
            session: 'Your session has expired. Sign in again.',
            offline: 'Could not reach the server.',
            name: 'Give the investment a name.',
            currentMark: 'The unrealised mark and its amount must be numbers, zero or more.',
            rounds: 'Cost basis amounts, valuations and units must be numbers, zero or more.',
            distributions: 'Distribution amounts must be numbers, zero or more.',
        },
    },

    accounts: {
        aria: 'Accounts',
        ibkrIndex: 'IBKR (Kanosei Index)',
        ibkr: 'IBKR',
        ibkrOther: 'IBKR (other)',
        fidelity: 'Fidelity',
        crypto: 'Crypto',
        private: 'Private',
        tagIndex: 'index',
        tagOther: 'other',
        tagWip: 'WIP',
        cryptoWip: 'P/L breakdown in progress: Arbitrum and Base history is incomplete, so net P/L and deposits are not shown yet. The value is live and complete.',
        cryptoHistory: 'Daily history recorded from {date}.',
        cryptoScopeWip: 'Crypto wallets are in the value from {date}, when tracking began; their P/L breakdown is a work in progress.',
        noHistory: 'This broker reports no daily history or period breakdown.',
        noSplit: 'Mark to market and realised are not separable for this broker \u2014 it reports no cost basis, so only their combined effect is known, and it is already inside net P/L.',
        noHistoryScope: 'No daily history — this broker does not report one.',
        partialHistoryScope: 'Excludes {names} — no daily history is reported for it.',
        privateLabel: 'Private investments',
        cryptoScope: 'Crypto wallets are in the value from {date}, when tracking began. Their breakdown runs from inception, so it is on the Crypto card and view rather than here.',
        cryptoNote: 'Valued from the chain. Breakdown since inception, on All; daily history recorded from {date}.',
        cryptoInception: 'Since the first on-chain record. Tokens that arrive without payment — staking rewards and airdrops included — count as deposits at their value on arrival; gas is inside mark to market. Daily history recorded from {date}.',
        privateScope: 'Private investments are in the value at their recorded marks from {date}. Their breakdown runs from the first investment, so it is on the Private card and view rather than here.',
        privateNote: 'At the marks on the Private Investments tab. Breakdown since the first investment, on All; daily history recorded from {date}.',
        privateInception: 'Since the first investment. Exits count as withdrawals at their exit mark; an investment with no mark is left out of the P/L. Daily history recorded from {date}.',
    },

    timeframe: {
        aria: 'Period',
        '1d': '1D',
        '1w': '1W',
        // Month to date rather than a rolling month: it is the window the
        // broker decomposes, and the one the figures below actually cover.
        mtd: 'MTD',
        '3m': '3M',
        '6m': '6M',
        ytd: 'YTD',
        '1y': '1Y',
        all: 'All',
    },

    positions: {
        symbol: 'Symbol',
        name: 'Name',
        account: 'Account',
        sector: 'Sector',
        qty: 'Qty',
        price: 'Price',
        value: 'Value',
        unrealised: 'Unrealised',
        unrealisedPct: 'Unrealised %',
        pctNav: '% NAV',
        opened: 'Opened',
        cashTag: '(CASH)',
        count_one: '{count} position',
        count_other: '{count} positions',
        filterAria: 'Filter positions',
        viewAria: 'Positions view',
        heatmapLegend: 'Area: position size · Colour: unrealised P/L vs cost',
        heatmapEmpty: 'No positions match this filter.',
    },
    crypto: {
        title: 'Crypto Holdings',
        scopeAria: 'Wallets',
        count_one: '{count} holding',
        count_other: '{count} holdings',
        members_one: '{count} position',
        members_other: '{count} positions',
        chainCount_one: '{count} chain',
        chainCount_other: '{count} chains',
        walletCount_one: '{count} wallet',
        walletCount_other: '{count} wallets',
        expand: 'Show the {symbol} breakdown',
        collapse: 'Hide the {symbol} breakdown',
        balancesAria: 'Balance size',
        balancesMain: 'Holdings',
        balancesSmall: 'Balances < $100, Total: ${total}',
        pnlLine: 'Unrealised P/L {pnl} USD ({pct})',
        pnlPartial_one: '{count} holding has no entry price yet and is left out of it.',
        pnlPartial_other: '{count} holdings have no entry price yet and are left out of it.',
        indexing: 'Indexing on-chain history… Entry prices and purchase dates appear when it finishes.',
        empty: 'Nothing held above $1 in the tracked wallets.',
        hidden_one: '{count} token not shown: no market price, or worth under $1.',
        hidden_other: '{count} tokens not shown: no market price, or worth under $1.',
        method: 'Average cost. A swap is priced by what was paid for it; tokens that arrive without payment are valued at the market price on arrival; transfers between tracked wallets keep their original cost and dates. Aave supplies and borrows are shown as the asset itself (a borrow as a negative amount); liquid-staking tokens are labelled with their issuer. Hyperliquid is shown in spot terms: each long perp as the asset held at its entry and notional value, shorts left in the USDC, and USDC as the account\'s equity less its longs. Liquidity-pool positions are not included.',
        col: {
            token: 'Token',
            chain: 'Chain',
            wallet: 'Wallet',
            qty: 'Qty',
            avgEntry: 'Avg entry',
            price: 'Price',
            value: 'Value',
            pnl: 'P/L',
            pnlPct: 'P/L %',
            firstPurchase: 'First purchase',
            lastPurchase: 'Last purchase',
            held: 'Held',
        },
        held: {
            days: '{n}d',
            months: '{m}mo',
            yearsMonths: '{y}y {m}mo',
        },
        chains: {
            ethereum: 'Ethereum',
            arbitrum: 'Arbitrum',
            base: 'Base',
            solana: 'Solana',
            hyperliquid: 'Hyperliquid',
        },
        kind: {
            staked: '{protocol} · staked',
            supply: '{protocol} · supplied',
            borrow: '{protocol} · borrowed',
            cash: '{protocol} · cash',
        },
        openOn: 'Open {symbol} on {site}',
        wallets: {
            heading: 'Wallets',
            empty: 'No wallets yet. Add an address below to start.',
            chainAria: 'Chain',
            addressAria: 'Wallet address',
            addressPlaceholder: 'address…',
            labelAria: 'Wallet label',
            labelPlaceholder: 'label (optional)',
            add: 'add wallet',
            remove: 'remove',
            removeAria: 'Remove wallet {name}',
            confirmRemove: 'Stop tracking {name}? The history indexed for it is deleted too.',
            synced: 'synced {when}',
            indexing: 'indexing history…',
            notConfigured: 'not configured: the {provider} API key is missing',
            syncError: 'last sync failed; will retry',
            balanceError: 'balance could not be read just now',
            rename: 'rename',
            nameAria: 'Wallet name',
        },
        errors: {
            load: 'Could not load crypto holdings.',
            save: 'Could not add the wallet.',
            remove: 'Could not remove the wallet.',
            rename: 'Could not rename the wallet.',
            sync: 'Could not index the on-chain history. Refresh to try again.',
            badAddress: 'That is not an address on the chosen chain.',
            duplicate: 'That wallet is already tracked.',
            invalid: 'Check the chain and the address.',
            session: 'Your session has expired. Please sign in again.',
            offline: 'Could not reach the server.',
        },
    },
    themes: {
        column: 'Theme',
        notAssigned: 'Not assigned',
        manage: 'themes',
        empty: 'No themes yet. Add one, then pick it from the Theme column.',
        newPlaceholder: 'new theme…',
        newAria: 'New theme name',
        add: 'add',
        deleteAria: 'Delete theme {name}',
        assignAria: 'Theme for {symbol}',
        confirmDelete: 'Delete the theme "{name}"?',
        confirmDeleteAssigned_one: 'Delete the theme "{name}"? {count} asset will be set to Not assigned.',
        confirmDeleteAssigned_other: 'Delete the theme "{name}"? {count} assets will be set to Not assigned.',
        errors: {
            load: 'Could not load themes.',
            save: 'Could not save the theme.',
            delete: 'Could not delete the theme.',
            duplicate: 'A theme with that name already exists.',
            gone: 'That theme no longer exists. Reload the page to see the current list.',
            session: 'Your session has expired. Please sign in again.',
            offline: 'Could not reach the server.',
        },
    },

    activity: {
        title: 'Transactions',
        error: 'Could not load transaction history.',
        emptyBefore: 'No transactions. Link Fidelity in',
        emptyLink: 'settings',
        emptyAfter: '.',
        scope: 'Fidelity only — the Interactive Brokers Flex query reports positions and NAV, not trades.',
        date: 'Date',
        type: 'Type',
        symbol: 'Symbol',
        description: 'Description',
        qty: 'Qty',
        price: 'Price',
        amount: 'Amount',
        fee: 'Fee',
        count_one: '{count} transaction',
        count_other: '{count} transactions',
        net: 'net {amount} {currency}',
        filterAria: 'Filter transactions',
        showMore: 'show {count} more',
        showing: 'showing {shown} of {total}',
        group: {
            trades: 'Trades',
            income: 'Income',
            transfers: 'Transfers',
            fees: 'Fees & tax',
            options: 'Options',
        },
    },

    notices: {
        cryptoUnconfiguredMany: 'Crypto: {count} wallets are not included — {chains} not configured on the server.',
        cryptoUnconfiguredOne: 'Crypto: {name} is not included — {chain} is not configured on the server.',
        cryptoSomeUnreadable: 'Some crypto wallets could not be read; showing their last known balances.',
        cryptoUnreadable: 'Crypto wallets could not be read; showing their last known balances.',
        cryptoCurrency: 'Crypto wallets are not included: they are valued in USD and the book is in {ccy}.',
    },

    notes: {
        title: 'Notes',
        error: 'Could not load notes.',
        noticesHeading: 'Notices',
        noticesEmpty: 'Nothing needs attention.',
        dismiss: 'dismiss',
        restore: 'restore',
        showDismissed: 'show {count} dismissed',
        hideDismissed: 'hide {count} dismissed',
        heading: 'Notes',
        lede: 'Shared with everyone signed in. For the reasoning a position does not carry on its own — why something is overweight, what a decision was waiting on.',
        placeholder: 'Write a note…',
        add: 'Add note',
        empty: 'No notes yet.',
        edited: 'edited',
        newAria: 'New note',
        editAria: 'Edit note',
    },

    index: {
        title: 'Kanosei Index',
        tabs: {
            portfolio: 'Portfolio',
            rebalance: 'Rebalance',
            targets: 'Target Weights',
            rules: 'Rules',
        },
        error: 'Could not load the index.',
        scope: 'Measured on the {account} account.',
        sleeveOver_one: '{names} names in the asset sleeve ({count} added at a capital addition) — targets sum to {target}% until the next reconstitution.',
        sleeveOver_other: '{names} names in the asset sleeve ({count} added at capital additions) — targets sum to {target}% until the next reconstitution.',
        deployment: '{pct}% invested · {cash} {currency} uninvested cash.',
        ranking: 'ranking {date}',
        tabsAria: 'Index views',
        view: {
            aria: 'Index view',
            all: 'All',
            assetSleeve: 'Asset sleeve',
            growthEtfs: 'Growth ETFs',
            empty: 'Nothing held in this sleeve.',
            legend: 'Area: position size · Colour: drift vs target weight',
            liveRank: 'Live rank',
            target: 'Target',
            // The master portfolio's `positions.pctNav` really is a share of
            // NAV. The index's is a share of invested capital, so it needs a
            // label of its own rather than borrowing that one.
            pctInvested: '% invested',
        },
    },

    countdown: {
        bandCheck: 'Band check',
        reconstitution: 'Reconstitution',
        today: 'today',
        daysLeft_one: '{count} day left',
        daysLeft_other: '{count} days left',
        withinBand: 'all positions within band',
        outsideBand_one: '{count} position outside band',
        outsideBand_other: '{count} positions outside band',
        replaced_one: '{count} constituent would be replaced',
        replaced_other: '{count} constituents would be replaced',
        noChanges: 'no constituent changes pending',
    },

    performance: {
        netPl: 'Net P/L',
        netDeposits: 'Net deposits',
        notEarned: 'contributed, not earned',
        markToMarket: 'Mark to market',
        realised: 'Realised',
        dividends: 'Dividends & interest',
        fees: 'Fees & commissions',
        accruals: 'Accruals & FX',
        changeInValue: 'Change in value',
        changePct: '{pct}% change in value',
        twr: '{pct}% time-weighted return',
        onCapital: '{pct} on capital employed',
        period: '{from} to {to}',
        noBreakdown: 'Change in value only — the broker reports a breakdown for month to date, year to date and the full period, and for no other window.',
        noBreakdownInception: 'Change in value only — this breakdown runs from inception. Choose All to see it.',
    },

    navChart: {
        title: 'Portfolio value',
        change: '{amount} {currency} change in value',
        changeWithDeposits: '{amount} {currency} change in value, deposits included',
    },

    breakdown: {
        title: 'Breakdown',
        aria: 'Breakdown view',
        chartAria: 'Allocation breakdown',
        assetType: 'Asset type',
        sector: 'Sector',
        country: 'Country',
        currency: 'Currency',
        bars: 'Bars',
        pie: 'Pie',
        heatmap: 'Heatmap',
        unclassified: 'Unclassified',
        legend: 'Area: share of portfolio · Colour: unrealised P/L vs cost',
        pnlTitle: 'Unrealised P/L vs cost',
        noData: 'No {dimension} data yet — brokers do not report it, so it needs a market-data provider.',
    },

    rebalance: {
        asset: 'Asset',
        current: 'Current',
        target: 'Target',
        drift: 'Drift',
        band: 'Band',
        trade: 'Trade',
        nextAction: 'Next action',
        ok: 'Every position is inside its band. No action at the next monthly check.',
        breaching_one: '{count} position outside band — {symbols}',
        breaching_other: '{count} positions outside band — {symbols}',
        leaving_one: '{symbols} is outside the live top 20 and would be replaced at the next reconstitution. Weights stay frozen until then.',
        leaving_other: '{symbols} are outside the live top 20 and would be replaced at the next reconstitution. Weights stay frozen until then.',
        bandTitle: 'Band {low}%–{high}% of invested capital',
        liveRank: '#{rank} live',
        entered: 'joined at #{rank}',
        shares: '{count} sh',
        footnote: 'Bands are relative to target: ±{asset}% for the asset sleeve, and +{growth}% for growth ETFs, which carry an upper band only — a sector below target waits for new capital rather than being funded by a trim. Anything above {cap}% is trimmed back to {cap}% regardless. Weights are a share of invested capital: cash is excluded, so an uninvested balance cannot move every weight at once. A band breach sizes back to target and a cap breach to the cap — never to the band edge — and neither anticipates an incoming contribution. Values in {currency}.',
    },

    targets: {
        symbol: 'Symbol',
        asset: 'Asset',
        marketCap: 'Market cap',
        weight: 'Weight',
        status: 'Status',
        theme: 'Theme',
        current: 'Current',
        growthSleeve: 'Growth sleeve — {pct}%',
        held: 'held',
        notHeld: 'not held',
        newConstituent: 'new constituent',
        unavailable: 'Market-cap ranking unavailable.',
        unavailableWhy: 'Market-cap ranking unavailable — {reason}.',
        lede: 'Projected for the {date} reconstitution, on the ranking as of {asOf}.',
        entering: 'Entering: {symbols}.',
        leaving: 'Leaving: {symbols}.',
        assetSleeve: 'Asset sleeve',
        growthSleeveRow: 'Growth sleeve',
        growthLede: 'Not ranked by market cap. Five themes at 2% each, reviewed on breadth, liquidity and purity rather than trailing return.',
    },

    // Rendered from IndexRow.action + actionParams, so the Next Action column
    // translates. `detail` from the API is the English fallback.
    action: {
        hold: 'Within band',
        trim_band: '{rel}% over target, outside the ±{band}% band — trim to {target}%',
        add_band: '{rel}% under target, outside the ±{band}% band — add to {target}%',
        trim_cap: 'Above the {cap}% cap — trim back to {cap}%',
        buy_missing: 'Not held — buy to {target}%',
        sell_not_in_index: 'Held but not an index constituent — sell',
        // A `hold` that is nonetheless outside the live top 20. Same action,
        // different sentence — chosen on actionParams.leaving.
        hold_leaving: 'Within band, but outside the live top 20 — replaced at the next reconstitution',
        label: {
            hold: 'Hold',
            trim_band: 'Trim',
            add_band: 'Add',
            trim_cap: 'Trim (cap)',
            buy_missing: 'Buy',
            sell_not_in_index: 'Sell',
        },
    },

    settings: {
        title: 'Linked accounts',
        empty: 'No accounts linked yet.',
        managed: 'managed',
        managedTitle: 'Configured in AWS Secrets Manager',
        unlink: 'unlink',
        rename: 'rename',
        nicknameAria: 'Connection nickname',
        query: 'query {ref}',
        linkIbkr: 'Link Interactive Brokers',
        linkFidelity: 'Link Fidelity',
        label: 'Label',
        queryId: 'Query ID',
        token: 'Token',
        linkAccount: 'Link account',
        loading: 'Loading…',
        linking: 'Linking…',
        opening: 'Opening…',
        connectFidelity: 'Connect Fidelity',
        linkedNotice: 'Account linked. Positions will appear after the next refresh.',
        indexAccount: 'Kanosei Index account',
        indexAccountHelp: 'One Interactive Brokers Flex query can cover several accounts, and only one of them is the index. Everything on the index page — weights, drift, rebalance and reconstitution — is measured on the account chosen here, and holdings in any other account are ignored.',
        indexAccountLabel: 'Index account',
        indexAccountAuto: 'Choose automatically',
        indexAccountDerived: 'Currently using {account}, chosen because its holdings match the index. Pick it explicitly to stop it moving.',
        indexSavedNotice: 'Index account saved.',
        fidelityLinkedNotice: 'Fidelity connected. Holdings appear once the brokerage finishes its first sync, which can take a few minutes.',
        // Split around the emphasised run rather than carrying markup in the
        // string. The emphasis sits mid-sentence in both languages.
        ibkrBefore: 'In IBKR: ',
        ibkrPath: 'Performance & Reports \u2192 Flex Queries',
        ibkrAfter: '. Create an Activity Flex Query including Open Positions (with lot detail), Trades and Cash Report, then generate a token under Flex Web Service Configuration. The token is read-only \u2014 it cannot trade or move funds.',
        fidelityBefore: 'Fidelity has no public API, so it connects through ',
        fidelityBrand: 'SnapTrade',
        fidelityAfter: "'s hosted portal. You sign in to Fidelity on their page \u2014 no Fidelity password is ever entered here or stored by us. The connection is read-only and cannot place trades.",
    },

    // The public site.
    //
    // Artwork TITLES and ARTIST names are deliberately absent from this
    // catalogue and always will be: they are the identity of a work, not copy.
    // Only the chrome around them lives here.
    home: {
        heroSub: 'Kanosei holds a concentrated book across public markets, private companies and digital art.',
        viewCollection: 'View the collection',
        // *…* marks an emphasised run, rendered as <em> by Rich.
        thesisHeading: 'We allocate where we have a *view*, not to fill a mandate',
        thesisP1: 'Kanosei is a private investment fund. The book is concentrated and spans equities, crypto, private companies and digital art — all aimed to express our views on where the world (and capital) is heading towards.',
        thesisP2: 'We operate without a fixed mandate as we believe the world is changing at hyperspeed and can no longer adhere to inelastic assumptions.',
        collectionLabel: 'The collection',
        collectionHeading: 'A record of NFTs as they were, as they are, as they will be',
    },

    collection: {
        // {title} and {artist} are interpolated verbatim, never translated.
        alt: '{title} by {artist}',
        artistsCounted: 'Unique artists represented in our collection',
        worksCounted: 'Artworks collected',
        heading: 'We collect and invest in digital art, using the blockchain as a trustless landscape for creation.',
        p1: 'Provenance used to depend on institutions — a gallery\u2019s word, a certificate in a drawer, a chain of custody someone had to vouch for. On-chain, it is simply a fact: who made a work, when, and everyone who has held it since, verifiable by anyone and belonging to no one. That is the shift we are collecting through. It removes the gatekeeper from the space between an artist and the person who values their work, and it makes the record itself part of the medium.',
        p2: 'The collection spans a breadth of works from ones that translate traditional mediums into digital, such as those by Trevor Jones and Reuben Wu, to ones that explore the possibilities of digital art, such as generative works by Casey Reas and Zeblocks via ArtBlocks. Works that capture social phenomenon and shared culture, such as Marvel, anime, memes, games, and beautiful scene scapes, are also a big part of the collection.',
        p3: 'As the NFT space burgeons and shifts the traditional contexts, we aim to discover works that explore new paradigms in how art can be created and altered, applied to the metaverse as well as support works of new artists and of those from minority communities.',
        p4: 'Individually, the works we collect reflect the unique artist\u2019s styles and journey.',
        p5: 'As a whole, our collection is a record, a documentation of NFTs as they were, as they are, as they will be.',
        // The two names and their handles are interpolated, not translated.
        curatedBy: 'Curated by',
        and: 'and',
        goToArtwork: 'Go to artwork',
    },

    work: {
        by: 'By:',
        viewArtwork: 'View artwork',
    },

    footer: {
        rights: '\u00a9 {year} Kanosei All Rights Reserved',
    },

    login: {
        title: 'Portfolio',
        signIn: 'Sign in with Google',
        blurb: 'This area is restricted to Kanosei accounts. Sign in with your @kanosei.xyz Google account to continue.',
        domain: 'Requires an {domain} account.',
    },

    rules: {
        error: 'Could not load the index definition.',
        intro: 'How the Kanosei Index is operated. Constituents and weights are read live from the index definition and the current ranking; the thresholds are the agreed ruleset.',
        pendingLabel: 'Not yet in the engine',
        pending: 'Half-gap correction, the sector caps, and the profit take. A band breach is corrected all the way back to target rather than halfway; everything else on this page is live, including the ±{band}% band, the one-sided sector band, the hard cap trimming to the cap, and cash being out of the weight denominator. This page describes the approved rules either way.',

        cadence: 'Cadence',
        cadenceLede: 'Two rhythms, both anchored to inception on {inception}. Six monthly checks, then a reconstitution that also serves as the sixth.',
        bandCheck: 'band check',
        reconCull: 'reconstitution + cull',

        sleeves: 'Two isolated sleeves',
        sleevesLede: 'Measured separately, and — with profit-taking as the single exception — funded separately. Capital additions are the only event that moves the split back toward {asset} / {growth}.',
        assetSleeve: 'Top 20 assets',
        assetSleeveNote: 'Can hold more than twenty. A contribution buys the top 20 as ranked that day, so any name newly in the ranking joins the sleeve without displacing what is already there. The cull at each reconstitution returns it to exactly twenty.',
        growthSleeve: 'Emerging sectors',
        growthSleeveNote: 'Never more than {max}, at an equal share each.',

        schedule: 'The weight schedule',
        scheduleLede: 'Weights attach to a rank, never to a name — a reconstitution changes which asset sits at each rank, not the ladder.',
        rankingAsOf: 'Ranking as of {date}.',
        unheldNote: 'Dimmed — in the current top 20, not currently held.',

        bands: 'Bands, drawn to scale',
        // *…* marks an emphasised run. Kept in the string rather than split
        // into before/em/after keys, because the emphasis falls in a different
        // place in each language.
        bandsLede: 'Each holding may drift ±{band}% of its target before anything happens. A breach closes *half the gap* to target — not the whole gap, and not back to the band edge. Shown for rank 1 at {top}.',
        bandsWhy: 'Half rather than whole, because a full correction harvests the entire run out of anything with a tailwind. Half rather than to-the-edge, because the edge is a fixed line a runner keeps moving away from: a position at twice its target loses 35% of itself correcting to the edge against 25% closing half the gap. Positions therefore live away from target most of the time, by design.',
        addBack: 'add back',
        target: 'target',
        trim: 'trim',
        hardCap: 'hard cap, any one name',
        hardCapNote: 'Checked before the band and taking priority over it, trimmed to the cap rather than to target. The only thing bounding a single position now that the band corrects slowly — at a sustained 20% monthly outperformance, rank 1 settles exactly here.',
        cashNote: 'Cash is excluded from every band check, and from the denominator every weight is measured against. It is shown in the holdings table but it is not a constituent.',
        sectorBandNote: 'Sector ETFs carry an upper band only. Above it a sector is trimmed like anything else; below target it is left alone, because a sector is never topped up by selling something else.',

        adding: 'Adding capital',
        addingLede: 'The only event that treats both sleeves the same, and the only one that moves the split back toward {asset} / {growth}.',
        whatBought: 'What is bought',
        whatBoughtV: 'The eligible top 20 as ranked that day, at the weights above, plus an equal share to each active sector',
        alreadyHeld: 'Already held?',
        alreadyHeldV: 'Topped up at whatever rank it occupies that day. Being held already is not a reason to skip it',
        existing: 'Existing holdings',
        existingV: '*Untouched.* Nothing is sold and no winner is cut back — an addition deploys new money and never rebalances the book',

        sectors: 'Emerging sectors',
        sectorsLede: 'A sector is a theme, not a position — Robotics is two ETFs, Space is two. Every cap and threshold measures the theme total.',
        sectorRules: 'Rules',
        perSector: 'per sector',
        perSectorNote: 'Trimmed back to the cap. Proceeds to the other sectors.',
        allFive: 'all five combined',
        allFiveNote: 'Trimmed pro-rata. Proceeds portfolio-wide — this one cannot be absorbed internally.',
        profitTake: 'profit take trigger',
        profitTakeNote: 'At twice its target, {pct} of the theme is sold each month it stays there.',
        sectorFundingNote: 'Sectors are added to out of new capital only. Nothing is ever sold to top one up, so a sector below target stays there until the next injection, where it takes its ordinary share like every other.',

        monthly: 'What happens at a monthly check',
        monthlyLede: 'All weights are measured once, from the opening snapshot. The rules run in this order and produce one combined order list — a trade that pushes something else out of band is caught next month, not in a second pass.',
        step: {
            membership: 'Sector membership',
            membershipV: 'Add or remove a sector. Never more than {max}. A removal without a replacement splits its capital equally across the survivors and resets their targets.',
            perSector: 'Per-sector cap',
            perSectorV: 'A theme above {pct}% of the book is trimmed back to the cap.',
            sleeve: 'Sleeve cap',
            sleeveV: 'All five above {pct}% are trimmed pro-rata — a sleeve that is too large cannot be fixed from inside itself.',
            profit: 'Profit take',
            profitV: 'A theme at or above {mult}× its target is trimmed by {pct}% of its value. Repeats every month it stays there.',
            bands: 'Asset bands',
            bandsV: 'Anything outside ±{band}% of its effective target closes half the gap to target, funded and absorbed within the asset sleeve.',
        },

        cash: 'Where the cash goes',
        cashLede: 'The rule that makes the sleeves isolated — and the two deliberate exceptions that move capital out of the speculative sleeve for good.',
        event: 'Event',
        clause: 'Clause',
        destination: 'Destination',
        route: {
            bandTrim: 'Asset band trim',
            cull: 'Six-month cull',
            sectorRemoved: 'Sector removed, not replaced',
            capBreach: 'Per-sector cap breach',
            topUp: 'Sector top-up',
            sleeveBreach: 'Sleeve cap breach',
            profitTake: 'Profit take at 2× target',
            assetSleeve: 'Asset sleeve',
            remainingSectors: 'Remaining sectors',
            otherSectors: 'Other sectors',
            newCapitalOnly: 'New capital only',
            wholePortfolio: 'Whole portfolio',
        },

        cull: 'The six-month cull',
        cullLede: 'Because every capital addition buys the top 20 *as ranked that day*, the sleeve holds more than twenty names between reconstitutions. At each reconstitution it is cut back to exactly twenty.',
        whoStays: 'Who stays',
        whoStaysV: 'The eligible top 20 by market cap on the reconstitution date. Nothing else counts — not how long a name has been held, nor how many checks it has spent in the top 20',
        whoGoes: 'Who goes',
        whoGoesV: 'Everything outside that top 20 on the day, however long it has been held and however close to the line it finished',
        weights: 'Weights',
        weightsV: 'Every surviving position resets to the target for its rank, and rank is read on the day — a name that has moved from #7 to #3 takes rank 3\'s weight',
        correction: 'Correction',
        correctionV: 'Full, not half. This is the one point where accumulated drift is cleared',
        sectorsExempt: 'Sectors',
        sectorsExemptV: 'Exempt. They carry their drift across the boundary and are never periodically reset',
    },

    sections: {
        start: 'Starting value',
        current: 'Current value',
        pnl: 'P/L',
        needsInput: 'needs input',
        editStart: 'Edit starting value',
        inclDeposits: 'incl. {amount} deposited',
        partsNeedInput_one: '{count} part needs input',
        partsNeedInput_other: '{count} parts need input',
        since: 'since {date}',
        part: 'Holding',
        coins: 'Coins',
        nfts: 'NFTs',
        privateBook: 'Private investments',
    },

    stocks: {
        title: 'Stock Investments',
        discretionary: 'Discretionary Investments',
        bucketsAria: 'Buckets',
        addBucket: '+ bucket',
        bucketName: 'Bucket name',
        emptyBucket: 'Nothing in this bucket.',
        allocations: 'Bucket allocations',
        split: 'Split',
        editSplit: 'Split {symbol} across buckets',
        addSplit: '+ add bucket',
        bucket: 'Bucket',
        shares: 'Shares',
        costBasis: 'Cost basis (USD)',
        errors: {
            load: 'Could not load the buckets.',
            refresh: 'Could not refresh just now.',
            bucket: 'Could not add the bucket.',
            numbers: 'Shares and cost basis must be numbers, zero or more.',
            duplicate: 'Each bucket can appear once.',
            tooMany: 'The split adds up to more shares than are held.',
            save: 'Could not save the split.',
        },
    },

    benchmarks: {
        title: 'Returns vs benchmarks',
        index: 'Kanosei Index',
        spx: 'S&P 500',
        ndx: 'NASDAQ',
        stocks: 'Stock Investments',
        modeAria: 'Chart',
        value: 'Value',
        compare: 'vs S&P 500 / NASDAQ',
        unavailable: 'No comparison for this period yet.',
    },

    nft: {
        col: {
            collection: 'Collection',
            qty: 'Qty',
            invested: 'Value invested',
            value: 'Current value',
            pricedAt: 'Floor as of',
            pnl: 'P/L',
            actions: 'Actions',
        },
        items_one: '{count} item',
        items_other: '{count} items',
        walletCount_one: '{count} wallet',
        walletCount_other: '{count} wallets',
        unknown_one: '{count} unknown',
        unknown_other: '{count} unknown',
        expand: 'Show {name} items',
        collapse: 'Hide {name} items',
        needsInput: 'needs input',
        needsInputAria: '{field}: needs input',
        viewTx: 'View the transaction',
        edited: 'Entered by hand',
        edit: 'Edit {name}',
    },
};

export default en;
